/**
 * VPR Database Layer
 *
 * Handles all API calls for VPR (Visual Place Recognition) management.
 * Follows the same pattern as RoomRecognition/utils/roomsDatabase.js
 */

import { getAuth } from 'firebase/auth';

// Isolated V2 endpoint. The legacy room recognition endpoint remains unchanged.
const VPR_ADMIN_URL = 'https://us-central1-ai-tourguide-1dc92.cloudfunctions.net/roomRecognitionV2Admin';

/**
 * Call the VPR admin Cloud Function
 */
async function callVprAPI(endpoint, method = 'GET', data = null) {
  const headers = {
    'Content-Type': 'application/json'
  };

  // V2 room metadata is private, so reads and writes both require admin auth.
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Authentication required. Please log in.');
  }
  const token = await user.getIdToken();
  headers['Authorization'] = `Bearer ${token}`;

  const url = `${VPR_ADMIN_URL}${endpoint}`;

  const options = {
    method,
    headers
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // Response wasn't JSON, use status-based message
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result;
}

/**
 * Convert image file to base64
 */
export async function fileToBase64(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const image = await new Promise((resolve, reject) => {
    const browserImage = new Image();
    browserImage.onload = () => resolve(browserImage);
    browserImage.onerror = () => reject(new Error('Could not decode the selected image'));
    browserImage.src = dataUrl;
  });
  const maxDimension = 1600;
  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image resizing is not supported by this browser');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.86).split(',')[1];
}

/**
 * Convert multiple files to base64
 */
export async function filesToBase64(files) {
  const promises = files.map(async file => {
    try {
      return await fileToBase64(file);
    } catch (error) {
      const filename = file?.name || 'Selected file';
      throw new Error(`${filename}: ${error?.message || 'Could not process this image'}`);
    }
  });
  return Promise.all(promises);
}

// ============================================================================
// VPR OPERATIONS
// ============================================================================

/**
 * Add a new room with reference images
 *
 * @param {Object} roomData - Room data
 * @param {string} roomData.roomId - Unique room identifier
 * @param {string} roomData.familyId - Shared guide-family ID
 * @param {string} roomData.guideId - Current language-specific guide ID
 * @param {string} roomData.museumName - Museum name
 * @param {string} roomData.roomName - Human-readable room name
 * @param {string} roomData.stopId - Associated stop ID (optional)
 * @param {File[]} imageFiles - Reference image files (3-20 recommended)
 * @param {Object} metadata - Additional metadata
 */
export async function addRoom(roomData, imageFiles, metadata = {}) {
  if (!imageFiles || imageFiles.length === 0) {
    throw new Error('At least one reference image is required');
  }

  if (imageFiles.length > 20) {
    throw new Error('Maximum 20 reference images allowed');
  }

  console.log(`[VprDB] Converting ${imageFiles.length} images to base64...`);
  const imagesBase64 = await filesToBase64(imageFiles);

  console.log(`[VprDB] Registering room with VPR service...`);
  const result = await callVprAPI('/add-room', 'POST', {
    roomId: roomData.roomId,
    familyId: roomData.familyId,
    guideId: roomData.guideId,
    museumName: roomData.museumName,
    roomName: roomData.roomName,
    stopId: roomData.stopId,
    images: imagesBase64,
    metadata
  });

  return result;
}

/**
 * Delete a room
 * @param {string} roomId - Room identifier
 * @param {string} venueId - Venue ID
 * @param {string} venueName - Museum/venue name
 */
export async function deleteRoom(roomId, familyId) {
  return callVprAPI(
    `/family/${encodeURIComponent(familyId)}/room/${encodeURIComponent(roomId)}`,
    'DELETE'
  );
}

/**
 * Get all rooms for a venue/museum
 * @param {string} venueId - Venue ID
 * @param {string} venueName - Museum/venue name
 */
export async function getVenueRooms(familyId) {
  console.log('[VprDB] getVenueRooms called with familyId:', familyId);
  const result = await callVprAPI(
    `/family/${encodeURIComponent(familyId)}/rooms`,
    'GET'
  );
  console.log('[VprDB] getVenueRooms response:', JSON.stringify(result));
  return result.rooms || [];
}

/**
 * Build or rebuild the CLIP index for a venue
 * @param {string} venueId - Venue ID
 * @param {string} venueName - Museum/venue name
 */
export async function buildIndex(familyId) {
  console.log('[VprDB] Publishing Room Recognition V2 for family:', familyId);
  return callVprAPI('/publish', 'POST', { familyId });
}

/**
 * Get index status for a venue
 * @param {string} venueId - Venue ID
 * @param {string} venueName - Museum/venue name
 */
export async function getIndexStatus(familyId) {
  return callVprAPI(`/status/${encodeURIComponent(familyId)}`, 'GET');
}

/**
 * Test VPR matching with an image
 * @param {File} imageFile - Image file to test
 * @param {string} venueId - Venue ID
 * @param {string} venueName - Museum/venue name
 */
export async function testMatch(imageFile, familyId, guideId) {
  const imageBase64 = await fileToBase64(imageFile);

  return callVprAPI('/test-match', 'POST', {
    image: imageBase64,
    familyId,
    guideId
  });
}

export default {
  addRoom,
  deleteRoom,
  getVenueRooms,
  buildIndex,
  getIndexStatus,
  testMatch,
  fileToBase64,
  filesToBase64
};
